<template>
  <div class="prompt-container">
    <div class="prompt-header">{{ cleanPrompt }}</div>
  </div>
  <div class="image-preview-wrapper">
    <div v-if="image?.brandStyle?.name" class="brand-style-name">{{ image?.brandStyle.name }}</div>
    <div v-if="!imageUrl" class="image-loading">
      <BbLoadingIndicator />
    </div>
    <template v-else>
      <div class="image-container">
        <BaseImage :src="imageUrl" alt="user image preview" />
      </div>
    </template>
    <div class="image-actions">
      <BbBtn type="secondary" @click="$emit('download')">{{ t('common.download') }}</BbBtn>
      <BbBtn
        type="secondary"
        @click="$emit('remix', { ...image, userQuery: prompt || image.userQuery, url: imageUrl })"
      >
        {{ t('files.remix') }}
      </BbBtn>
      <BbBtn type="secondary" @click="$emit('close')">{{ t('common.cancel') }}</BbBtn>
    </div>
  </div>
</template>

<script setup>
import { computed, defineProps, defineEmits, ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';

import BaseImage from '~/components/common/BaseImage.vue';
import useImageGeneration from '~/composables/useImageGeneration';

const props = defineProps({
  imageId: {
    type: String,
    default: '',
  },
  prompt: {
    type: String,
    default: '',
  },
  inConversationContext: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['close', 'download', 'remix']);

const { t } = useI18n();
const { currentConversation, getUserImages, getUserImageById } = useImageGeneration();

const imageUrl = ref('');
const image = ref(null);

const cleanPrompt = computed(() => {
  const currentPrompt = props.prompt || image.value?.userQuery || '';
  return currentPrompt.replace(/<[^>]*>/g, '');
});

const fetchImage = async () => {
  const userConversationId = props.inConversationContext ? currentConversation?.value?.userId || '' : '';

  if (props.imageId) {
    const imageResp = await getUserImageById(props.imageId);
    image.value = imageResp;
    const images = await getUserImages({ ids: [props.imageId], forceRefresh: true, userConversationId });
    if (images.length > 0) {
      imageUrl.value = images[0].url;
    }
  }
};

onMounted(() => {
  fetchImage();
});
</script>

<style scoped lang="scss">
.prompt-container {
  min-height: 20%;
  justify-content: flex-start;
  align-items: center;
  display: flex;
  overflow: visible;
  width: 100%;
  position: relative;
  padding: $bb-spacing-4 0;

  .prompt-header {
    color: $bb-neutral-1;
    padding: 0;
    flex: 1;
    display: -webkit-box;
    -webkit-line-clamp: 5;
    -webkit-box-orient: vertical;
    overflow: hidden;
    word-wrap: break-word;
    overflow-wrap: break-word;
  }
}

.image-preview-wrapper {
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
  position: relative;

  .brand-style-name {
    position: absolute;
    top: $bb-spacing-4;
    left: $bb-spacing-4;
    color: $bb-neutral-0;
    font-size: 12px;
    font-weight: 500;
    padding: $bb-spacing-1 $bb-spacing-2;
    background-color: rgba(0, 0, 0, 0.5);
    border-radius: 4px;
    white-space: nowrap;
    z-index: 10;
    pointer-events: none;
  }
}

.image-container {
  display: flex;
  justify-content: center;
  align-items: center;
  background: #f5f5f5;
  min-height: 300px;
  max-height: 500px;
  overflow: hidden;

  :deep(.image-wrapper) {
    height: 500px !important;

    img {
      object-fit: contain;
    }
  }
}

.image-loading {
  height: 300px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f5f5f5;
}

.image-actions {
  display: flex;
  justify-content: flex-end;
  gap: $bb-spacing-2;
  padding: $bb-spacing-2;
}
</style>
